import { useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, download, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, PageHeader, Empty, Loading, StatusChip, inputCls, btn } from "../components/kit";
import { CertList } from "../components/RecordView";
import { SdsRow } from "./Sds";

function CertReview({ c, onDone }) {
  const [reason, setReason] = useState("");
  const act = async (decision) => {
    try { await api.post(`/certificates/${c.id}/review`, { decision, reason }); toast.success("Decision recorded."); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-3 border-t pt-4" data-testid={`cert-review-${c.id}`}>
      <p className="text-sm"><b>{c.user_name}</b> · submitted {fmtDateTime(c.submitted_at)}<span className="block text-muted-foreground">Open the original before deciding.</span></p>
      <input className={inputCls} placeholder="Reason (required unless accepting)" aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cert-review-reason" />
      <div className="flex flex-wrap gap-2">
        <button className={btn.primary} onClick={() => act("accepted")} data-testid="cert-accept-button">Accept</button>
        <button className={btn.ghost} onClick={() => act("needs_correction")} data-testid="cert-correction-button">Needs correction</button>
        <button className={btn.ghost} onClick={() => act("rejected")} data-testid="cert-reject-button">Reject</button>
      </div>
    </div>
  );
}

function CertTab() {
  const { data, loading, reload } = useApi("/certificates/queue");
  if (loading) return <Loading />;
  return <CertList certs={data || []} reload={reload} reviewSlot={(c) => <CertReview c={c} onDone={reload} />} />;
}

function SdsTab() {
  const { data, loading } = useApi("/sds?scope=review");
  if (loading) return <Loading />;
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button className={btn.ghost} data-testid="sds-export-button" onClick={() => download("/sds-export", "DCBC_SDS_binder.zip").catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /> Export SDS binder (ZIP)</button></div>
      {(data || []).length === 0 ? <Empty>No SDS awaiting review.</Empty> : data.map((s) => <SdsRow key={s.id} s={s} />)}
    </div>
  );
}

function SafetyTab() {
  const { data, loading } = useApi("/safety-reports?scope=all");
  const [show, setShow] = useState("open");
  if (loading) return <Loading />;
  const rows = (data || []).filter((r) => show === "all" || r.status !== "closed");
  return (
    <div className="space-y-3">
      <select className={`${inputCls} max-w-xs`} aria-label="Show" value={show} onChange={(e) => setShow(e.target.value)} data-testid="safety-queue-filter"><option value="open">Open reports</option><option value="all">All reports</option></select>
      {rows.length === 0 ? <Empty>No open safety reports.</Empty> : rows.map((r) => (
        <Link key={r.id} to={`/reviews/safety/${r.id}`} data-testid={`safety-queue-${r.ref}`} className="flex flex-col gap-2 rounded-xl border bg-white p-4 hover:border-[color:var(--ink)] sm:flex-row sm:items-center">
          <div className="flex-1"><p className="font-semibold">{r.ref} · {r.category}{r.danger_remains === "yes" && <span className="ml-2 text-rose-700">Danger remains</span>}</p>
            <p className="text-sm text-muted-foreground truncate">{r.location} · {fmtDateTime(r.created_at)} · Owner {r.owner_name || "not assigned"}</p></div>
          <StatusChip status={r.status} />
        </Link>))}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const perms = user.permissions || [];
  const tabs = [
    (user.is_superadmin || user.is_manager) && ["certs", "Certificates", CertTab],
    (user.is_superadmin || perms.includes("sds_reviewer")) && ["sds", "SDS", SdsTab],
    (user.is_superadmin || perms.includes("safety_reviewer")) && ["safety", "Safety reports", SafetyTab],
  ].filter(Boolean);
  if (!tabs.length) return <Empty>You don't have any review permissions.</Empty>;
  return (
    <div>
      <PageHeader eyebrow="Reviewer" title="Review Queues" testid="reviews-header" />
      <Tabs defaultValue={tabs[0][0]}>
        <TabsList className="mb-6 h-auto flex-wrap">{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="tap px-5" data-testid={`reviews-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
